import Service from '@ember/service';
import { service } from '@ember/service';
import HashData from '../data/hashdata';
import Utils from '../utils/utils';

export default class HashService extends Service {
  @service tabService; // Don't delete, this is used dynamically
  @service dataService;
  @service validationService;

  /**
   * This function assumes that the hash config data is in order of dependency.
   * For example, at current time, the dac type parameter is required for any other parameter to be loaded from the hash
   */
  async loadFromHash(model) {
    const hashAtLoadValues = this.parseUrl(top.location.hash),
      processFn = (key, args) => {
        const paramInHash = key in hashAtLoadValues && !Utils.isNullOrWhiteSpace(hashAtLoadValues[key]);
        if (!paramInHash) return;

        const hashValue = hashAtLoadValues[key];

        if (args.handler && args.handler in this) this[args.handler](hashValue, args.model, args);
        else {
          const saveLocation = args.saveLocation || args.location,
            valueItem = Utils.getValue(args.valueItem, `${saveLocation}`);

          if (valueItem === hashValue) {
            return; // This value is already set
          }
          this.setProperty(args.valueItem, saveLocation, hashValue);
        }
      };

    if (Object.keys(hashAtLoadValues).length) {
      try {
        const configs = await this.loadRequiredParamsFromHash(model);
        this.processHashConfigData(configs, model, processFn);
        this.validationService.validateModel(model);
        this.updateHash(model);
      } catch (e) {
        console.error(`Loading from hash: An error occurred: ${e.message}. Defaults will be used.`);
        this.setHash({});
      }
    }
  }

  /**
   * Some assumptions are made here:
   *  All required params will not be dependent on non-required params
   *  Required params are in order of dependency
   *  Required params have handlers
   */
  async loadRequiredParamsFromHash(model) {
    const hashAtLoadValues = this.parseUrl(top.location.hash),
      requiredConfigs = HashData.configs.filter((config) => config.requiredForLoad);

    for (const requiredConfig of requiredConfigs) {
      const paramInHash =
        requiredConfig.key in hashAtLoadValues && !Utils.isNullOrWhiteSpace(hashAtLoadValues[requiredConfig.key]);

      if (!paramInHash) throw new Error(`Can't load from hash when missing key "${requiredConfig.key}"`);

      const requiredConfigHashValue = hashAtLoadValues[requiredConfig.key],
        result =
          requiredConfig.handler && requiredConfig.handler in this
            ? await this[requiredConfig.handler](requiredConfigHashValue, model)
            : null;

      if (!result)
        throw new Error(
          `Can't load from hash with invalid key (${requiredConfig.key}) value "${requiredConfigHashValue}"`,
        );
    }

    return HashData.configs.filter((config) => !config.requiredForLoad);
  }

  updateHash(model) {
    const localHash = {},
      processFn = (key, args) =>
        this.setHashItem(key, this.findLocationInItem(args.location, args.valueItem, args.isObject), localHash);

    this.processHashConfigData(HashData.configs, model, processFn);

    this.setHash(localHash);
  }

  processHashConfigData(configs, model, processFn) {
    configs.forEach((hashConfig) => {
      let locationValue = null;
      const topLocation = hashConfig.inModel ? model : this;

      if (hashConfig.location) {
        locationValue = Utils.getValue(topLocation, `${hashConfig.location}`);
      }

      if (hashConfig.hasMultipleInOneLocation && Array.isArray(locationValue)) {
        locationValue
          .filter((locationValueItem) => locationValueItem.id in hashConfig.items && locationValueItem.useInUrl)
          .forEach((locationValueItem) => {
            const subItem = hashConfig.items[locationValueItem.id];

            if (subItem.hasMultiple) {
              Object.keys(subItem.items)
                .map((key) => subItem.items[key])
                .forEach((subSubItem) =>
                  processFn(subSubItem.key, {
                    location: subSubItem.location,
                    saveLocation: subSubItem.saveLocation,
                    valueItem: locationValueItem,
                    handler: subSubItem.handler,
                    model: model,
                  }),
                );
            } else
              processFn(subItem.key, {
                location: hashConfig.itemLocation,
                valueItem: locationValueItem,
                more: hashConfig.more,
                handler: hashConfig.itemHandler,
                model: model,
              });
          });
      } else if (hashConfig.hasMultiple) {
        Object.keys(hashConfig.items)
          .map((key) => hashConfig.items[key])
          .forEach((hashConfigItem) =>
            processFn(hashConfigItem.key, {
              location: hashConfigItem.location,
              valueItem: model,
              more: hashConfigItem.more,
              handler: hashConfigItem.handler,
              model: model,
            }),
          );
      } else
        processFn(hashConfig.key, {
          location: hashConfig.location,
          valueItem: topLocation,
          more: hashConfig.more,
          handler: hashConfig.handler,
          model: model,
          isObject: hashConfig.isObject,
        });
    });
  }

  /**
   * Handlers
   */

  handleBooleanInputChange(newValue, model, args = {}) {
    if (newValue === 'true' || newValue === 'false') {
      const saveLocation = args.saveLocation || args.location,
        booleanValue = newValue === 'true',
        valueItem = Utils.getValue(args.valueItem, `${saveLocation}`);

      if (valueItem === booleanValue) {
        return;
      }
      this.setProperty(args.valueItem, saveLocation, booleanValue);
    }
  }

  handleNumericInputChange(newValue, model, args = {}) {
    if (Utils.isNumeric(newValue)) {
      const saveLocation = args.saveLocation || args.location,
        numericValue = Number(newValue),
        valueItem = Utils.getValue(args.valueItem, `${saveLocation}`);

      if (valueItem === numericValue) return; // This value is already set
      this.setProperty(args.valueItem, saveLocation, numericValue);
    }
  }

  handleSelectInputChange(newValue, model, args = {}) {
    const valueItem = Utils.getValue(args.valueItem, `${args.location}`);

    if (valueItem === newValue) return; // This value is already set

    const optionsLocation = Utils.getValue(model, `${args.more.optionsLocation}`);
    const foundSelectedOption = optionsLocation.find((option) => option.id === newValue);
    if (foundSelectedOption) {
      this.setProperty(args.valueItem, args.more.saveLocation, foundSelectedOption);
    } else {
      console.warn(
        `Loading from hash: ${args.more.description} value (${newValue}) is not valid for the current DAC Type.  The default will be used.`,
      );
    }
  }

  handleDacChange(newValue, model) {
    if (model.signalChainHandler.dac.selected.name.toLowerCase() === newValue.toLowerCase()) return; // This dac is already selected

    const newDac = model.signalChainHandler.dac.gridData.data.data.find(
      (item) => item.name.toLowerCase() === newValue.toLowerCase(),
    );
    if (!newDac) {
      console.warn(
        `Loading from hash: Dac (${newValue}) is not supported in the current Dac Type Pst (${model.signalChainHandler.dac.type.pst.id}).  The default will be used.`,
      );
      return;
    }

    model.signalChainHandler.dac.selected = newDac;
    model.handleDacChange(model.signalChainHandler.dac);
  }

  async handleDacTypeChange(newValue, model) {
    const option = model.signalChainHandler.dacTypeOptions.find(
      (typeOption) => typeOption.id.toLowerCase() === newValue.toLowerCase(),
    );
    if (!option) {
      console.warn(`Loading from hash: Dac Type Option (${newValue}) does not exist.`);
      return;
    }

    console.time('get dac grid data');
    const gridData = await this.dataService.getDatabookGridData(option.pst, model.urlHandler.homeUrl);
    console.timeEnd('get dac grid data');

    model.handleDacTypeChange(option, gridData);

    return model.signalChainHandler.dac.type;
  }

  handleObjectChange(newValue, model, args = {}) {
    let result = {};
    newValue.split(';').forEach((keyValuePair) => {
      const splitKeyValuePair = keyValuePair.split(':');
      if (splitKeyValuePair.length === 2) {
        if (Utils.isNumeric(splitKeyValuePair[1])) result[splitKeyValuePair[0]] = Number(splitKeyValuePair[1]);
        else result[splitKeyValuePair[0]] = splitKeyValuePair[1];
      }
    });

    this.setProperty(model, args.location, result);
  }

  handleTopologyChange(newValue, model, args = {}) {
    let currentInput = args.valueItem;
    if (currentInput.selectedOption.id.toLowerCase() === newValue.toLowerCase()) return; // this option is already selected

    const newOption = args.valueItem.options.find((option) => option.id.toLowerCase() === newValue.toLowerCase());
    if (!newOption) {
      console.warn(
        `Loading from hash: Topology Option (${newValue}) does not exist for Topology Input (${args.valueItem.id}).  The default will be used.`,
      );
      return;
    }

    if (newOption.isDisabled || newOption.isHidden) {
      console.warn(
        `Loading from hash: Topology Option (${newValue}) for Topology Input (${args.valueItem.id}) is not selectable.  The default will be used.`,
      );
      return;
    }

    currentInput.selectedOption = newOption;
    model.handleTopologyChange(currentInput);
  }

  handleSignalChainChange(newValue, model, args = {}) {
    let currentItem = args.valueItem;
    if (currentItem.selected.name.toLowerCase() === newValue.toLowerCase()) return; // This item is already selected

    const pstData = model.signalChainHandler.gridData[args.valueItem.config.pst.id].data;
    if (pstData && pstData.data) {
      const newItem = pstData.data.find((item) => item.name.toLowerCase() === newValue.toLowerCase());
      if (!newItem) {
        console.warn(
          `Loading from hash: Part (${newValue}) does not exist for Signal Chain pst (${args.valueItem.config.pst.id}). The default will be used.`,
        );
        return;
      }
      currentItem.selected = newItem;
    } else {
      console.warn(
        `Grid data could not be found for signal chain item (${args.valueItem.config.pst.name}) pst (${args.valueItem.config.pst.id})`,
      );
      return;
    }
  }

  handleTabChange(newValue) {
    if (this.tabService.selected.index == newValue) return; // We are already on the selected tab

    const newTab = this.tabService.getTabByIndex(newValue);
    if (!newTab) console.warn(`Loading from hash: Tab (${newValue}) does not exist. The default will be used.`);

    this.tabService.goToTab(newTab);
  }

  /**
   * Helpers
   */

  setHash(hash) {
    const hashStr = '#' + this.stringify(hash);
    if (hashStr != top.location.hash) {
      top.location.replace(hashStr);
    }
  }

  setHashItem(key, value, hash) {
    if (key && !Utils.isNullOrWhiteSpace(value)) {
      hash[key] = value;
    }
  }

  findLocationInItem(key, item, isObject = false) {
    const itemKey = Utils.getValue(item, `${key}`);
    if (isObject && key) {
      return Object.keys(itemKey)
        .filter((valueKey) => valueKey != 'lastUpdateDate')
        .map((valueKey) => `${valueKey}:${itemKey[valueKey]}`)
        .join(';');
    } else {
      return key ? itemKey : null;
    }
  }

  stringify(obj) {
    return obj
      ? Object.keys(obj)
          .sort()
          .map((key) => {
            let val = obj[key];

            if (Array.isArray(val))
              return val
                .sort()
                .map((x) => encodeURI(key) + '=' + encodeURI(x))
                .join('&');

            return encodeURI(key) + '=' + encodeURI(val);
          })
          .join('&')
      : '';
  }

  parseUrl(str) {
    if (typeof str !== 'string') {
      return {};
    }

    str = str.trim().replace(/^(\?|#|&)/, '');

    if (!str) {
      return {};
    }

    return str.split('&').reduce((ret, param) => {
      const parts = param.replace(/\+/g, ' ').split('=');
      let key = parts[0];
      let val = parts[1];

      key = decodeURIComponent(key);

      // missing `=` should be `null`:
      // http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
      val = val === undefined ? null : decodeURIComponent(val);

      if (!Object.prototype.hasOwnProperty.call(ret, key)) {
        ret[key] = val;
      } else if (Array.isArray(ret[key])) {
        ret[key].push(val);
      } else {
        ret[key] = [ret[key], val];
      }

      return ret;
    }, {});
  }

  setProperty(obj = {}, key, val) {
    const keys = key.split('.');
    const last = keys.pop();
    keys.reduce((o, k) => (o[k] ??= {}), obj)[last] = val;
  }
}
